import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbFiles } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { type AssetListPayload, parseAssetListPayload } from "./chiefSchema";

interface AssetListRendererProps {
  block: StreamingBlock;
}

export const AssetListRenderer = (props: AssetListRendererProps) => {
  const { t } = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<AssetListPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseAssetListPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {t("chief_assets_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().assets.length > 0}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <TbFiles class="size-4 shrink-0 text-brand" />
                <span class="text-sm font-medium text-primary-text">{t("chief_assets_card.title")}</span>
                <span class="ml-auto text-xs text-muted-text">
                  {data().assets.length === 1
                    ? t("chief_assets_card.results_one")
                    : t("chief_assets_card.results", { count: String(data().assets.length) })}
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().assets}>
                  {(asset) => (
                    <li class="px-3 py-2">
                      <span class="block truncate text-sm text-primary-text">{asset.name}</span>
                      <Show when={asset.description}>
                        <p class="mt-0.5 truncate text-xs text-muted-text">{asset.description}</p>
                      </Show>
                      <Show when={asset.labels?.length}>
                        <div class="mt-1 flex flex-wrap gap-1">
                          <For each={asset.labels}>
                            {(label) => (
                              <span class="rounded-full border border-primary-border px-2 py-0.5 text-xs text-muted-text">
                                {label}
                              </span>
                            )}
                          </For>
                        </div>
                      </Show>
                    </li>
                  )}
                </For>
              </ul>

              <Show when={data().hasMore}>
                <div class="border-t border-primary-border px-3 py-2 text-xs text-muted-text">
                  {t("chief_assets_card.has_more")}
                </div>
              </Show>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
